<template>
  <div class="lot-page">
    <div v-if="loading" class="page-state">Chargement du contenu de l’achat…</div>
    <div v-else-if="loadError" class="page-state page-state--error">
      <strong>Impossible de charger les objets</strong>
      <span>{{ loadError }}</span>
      <button type="button" @click="goBack">← Retour aux achats</button>
    </div>

    <template v-else>
      <header class="lot-header">
        <button type="button" class="back" @click="goBack">← Achats</button>
        <div class="lot-header__title">
          <p>Contenu de l’achat</p>
          <h1>{{ offer.title || `Achat #${offerId}` }}</h1>
          <span>#{{ offerId }}<template v-if="sourceLabel"> · {{ sourceLabel }}</template> · {{ formatMoney(offerCost) }}</span>
        </div>
        <div class="lot-header__stats">
          <strong>{{ items.length }}</strong>
          <span>objet{{ items.length > 1 ? "s" : "" }}</span>
          <small v-if="pendingSaves" class="saving">Enregistrement…</small>
          <small v-else-if="items.length" class="saved">✓ Tout est enregistré</small>
        </div>
      </header>

      <!-- Valeurs du lot : appliquées aux nouveaux objets -->
      <section class="card defaults">
        <h2>Valeurs du lot</h2>
        <p class="hint">Appliquées à chaque nouvel objet ; modifiables ensuite objet par objet.</p>
        <div class="defaults__grid">
          <label>Plateforme
            <ListPicker v-model="defaults.platform" list="models" placeholder="Plateforme du lot" create-placeholder="Nouvelle plateforme" allow-create />
          </label>
          <label>Pays / région
            <ListPicker v-model="defaults.country" list="countries" placeholder="Pays du lot" />
          </label>
          <label>Type par défaut
            <select v-model="defaults.type">
              <option value="game">Jeu</option>
              <option value="console">Console</option>
              <option value="device">Périphérique</option>
            </select>
          </label>
        </div>
      </section>

      <!-- Ajout : l'analyse propose, l'utilisateur valide -->
      <section class="card add">
        <h2>Ajouter des objets</h2>
        <div class="add__actions">
          <button v-if="canExtract" type="button" class="action action--ai" :disabled="analysing" @click="extractFromListing">
            ✨ Extraire depuis l’annonce
          </button>
          <label class="action action--ai action--file" :class="{ disabled: analysing }">
            📷 Photo du lot
            <input type="file" accept="image/*" :disabled="analysing" @change="analysePhoto" />
          </label>
          <button v-if="speechSupported" type="button" class="action" :class="{ 'action--recording': listening }" @click="toggleDictation">
            {{ listening ? "⏹ Arrêter la dictée" : "🎤 Dicter" }}
          </button>
          <button type="button" class="action" @click="togglePaste">📋 Coller une liste</button>
        </div>

        <div v-if="pasteOpen" class="paste">
          <textarea
            v-model="pasteText"
            rows="5"
            placeholder="Décrivez le contenu, une ligne par objet ou en texte libre&#10;ex. : console PC Engine loose avec 2 manettes, testée OK&#10;Bomberman HuCard sans notice"
          ></textarea>
          <div class="paste__footer">
            <small v-if="listening" class="recording">● Dictée en cours… parlez normalement</small>
            <small v-else>L’analyse découpe le texte en objets à valider.</small>
            <button type="button" class="primary" :disabled="!pasteText.trim() || analysing" @click="analyseText">Analyser</button>
          </div>
        </div>

        <form class="quick" @submit.prevent="addQuickItem">
          <input ref="quickInput" v-model.trim="quickTitle" placeholder="Ajout rapide : désignation puis Entrée" />
          <select v-model="quickType" aria-label="Type">
            <option value="game">Jeu</option>
            <option value="console">Console</option>
            <option value="device">Périphérique</option>
          </select>
          <button type="submit" class="primary" :disabled="!quickTitle">Ajouter</button>
        </form>

        <div v-if="analysing" class="analysing">
          <span class="spinner"></span> Analyse en cours… vous pouvez continuer à compléter les objets.
        </div>
        <p v-if="analysisError" class="error">{{ analysisError }}</p>

        <!-- Propositions à valider -->
        <div v-if="proposals.length" class="proposals">
          <div class="proposals__header">
            <strong>{{ proposals.length }} proposition{{ proposals.length > 1 ? "s" : "" }}</strong>
            <span>{{ analysisMessage }}</span>
          </div>
          <ul>
            <li v-for="proposal in proposals" :key="proposal.__id" :class="{ review: proposal.__needsReview }">
              <input v-model="proposal.__selected" type="checkbox" :aria-label="`Ajouter ${proposal.title}`" />
              <div class="proposal__body">
                <input v-model="proposal.title" class="proposal__title" />
                <small>
                  {{ typeLabel(proposal.type) }}<template v-if="proposal.model"> · {{ proposal.model }}</template>
                  <template v-if="proposal.quantity > 1"> · ×{{ proposal.quantity }}</template>
                  <template v-if="proposal.included?.length"> · inclus : {{ proposal.included.join(", ") }}</template>
                  <template v-if="proposal.working !== 'untested'"> · {{ workingLabel(proposal.working) }}</template>
                </small>
                <small v-if="proposal.__needsReview" class="review-note">À vérifier</small>
              </div>
            </li>
          </ul>
          <div class="proposals__footer">
            <button type="button" class="secondary" @click="proposals = []">Ignorer</button>
            <button type="button" class="primary" :disabled="!selectedProposals.length" @click="acceptProposals">
              Ajouter {{ selectedProposals.length }} objet{{ selectedProposals.length > 1 ? "s" : "" }}
            </button>
          </div>
        </div>
      </section>

      <!-- Objets du lot : chaque modification est enregistrée automatiquement -->
      <section class="items">
        <div class="items__header">
          <h2>Objets du lot</h2>
          <span>Enregistrement automatique à chaque modification</span>
          <button v-if="items.length > 1" type="button" class="link" @click="toggleAll">{{ allOpen ? "Tout réduire" : "Tout déplier" }}</button>
        </div>
        <p v-if="!items.length" class="empty">Aucun objet pour l’instant : utilisez l’extraction, la dictée, une liste ou l’ajout rapide.</p>

        <article v-for="(item, index) in items" :key="item.__id" class="item" :class="[`item--${item.__state}`, { 'item--open': item.__open }]">
          <button type="button" class="item__summary" :aria-expanded="item.__open" @click="toggleItem(item)">
            <img v-if="item.images?.length" :src="imageUrl(item, item.images[0])" alt="" />
            <span v-else class="item__thumb">📷</span>
            <span class="item__summary-text">
              <strong>{{ item.title || "Objet sans désignation" }}</strong>
              <small>{{ summaryMeta(item) }}</small>
            </span>
            <span class="item__badge" :class="`badge--${item.__state}`">
              {{ { saved: "✓", saving: "…", dirty: "…", error: "⚠", invalid: "!" }[item.__state] || "" }}
            </span>
            <span class="chevron" aria-hidden="true">{{ item.__open ? "▴" : "▾" }}</span>
          </button>
          <div v-if="item.__open" class="item__main">
            <div class="item__photos">
              <img v-if="item.images?.length" :src="imageUrl(item, item.images[0])" :alt="item.title" />
              <span v-else class="item__nophoto">📷</span>
              <label class="photo-button" :class="{ busy: item.__uploading }">
                {{ item.__uploading ? "Envoi…" : item.images?.length ? `+ photo (${item.images.length})` : "Ajouter une photo" }}
                <input type="file" accept="image/*" multiple :disabled="item.__uploading" @change="uploadPhotos(item, $event)" />
              </label>
            </div>

            <div class="item__fields">
              <div class="row row--title">
                <input v-model="item.title" class="title" placeholder="Désignation" @input="queueSave(item)" />
                <select v-model="item.type" aria-label="Type" @change="queueSave(item)">
                  <option value="game">Jeu</option>
                  <option value="console">Console</option>
                  <option value="device">Périphérique</option>
                </select>
              </div>

              <div class="row">
                <input v-model="item.model" list="lot-platforms" placeholder="Plateforme" @change="queueSave(item)" />
                <select v-model="item.condition" aria-label="État" @change="queueSave(item)">
                  <option value="">État : à renseigner</option>
                  <option v-for="value in conditions" :key="value">{{ value }}</option>
                </select>
              </div>

              <div class="field">
                <span>Fonctionnement</span>
                <div class="segmented">
                  <button v-for="state in workingStates" :key="state.value" type="button" :class="[{ active: item.working === state.value }, `w-${state.value}`]" @click="setField(item, 'working', state.value)">{{ state.label }}</button>
                </div>
              </div>

              <div class="toggles">
                <div v-for="flag in flagsFor(item)" :key="flag.field" class="field">
                  <span>{{ flag.label }}</span>
                  <div class="segmented segmented--small">
                    <button v-for="option in tristate" :key="option.value" type="button" :class="{ active: (item[flag.field] || '') === option.value }" @click="setField(item, flag.field, option.value)">{{ option.label }}</button>
                  </div>
                </div>
              </div>

              <div class="field">
                <span>Inclus avec l’objet</span>
                <div class="tags">
                  <span v-for="(tag, tagIndex) in item.included" :key="tag" class="tag">
                    {{ tag }} <button type="button" :aria-label="`Retirer ${tag}`" @click="removeTag(item, tagIndex)">×</button>
                  </span>
                  <input v-model="item.__tag" placeholder="ex. 1 manette, câble AV… (Entrée)" @keydown.enter.prevent="addTag(item)" @blur="addTag(item)" />
                </div>
              </div>

              <details class="more" :open="Boolean(item.description || item.country !== defaults.country)">
                <summary>Plus de détails</summary>
                <div class="row">
                  <input v-model="item.country" list="lot-countries" placeholder="Pays / région" @change="queueSave(item)" />
                  <input v-model="item.brand" placeholder="Marque (ex. NEC, Sega)" @change="queueSave(item)" />
                </div>
                <textarea v-model="item.description" rows="2" placeholder="Notes : défauts, remarques…" @input="queueSave(item)"></textarea>
              </details>
            </div>
          </div>

          <footer v-if="item.__open" class="item__footer">
            <small class="state">
              <template v-if="item.__state === 'saving'">Enregistrement…</template>
              <template v-else-if="item.__state === 'error'">⚠ Non enregistré — <button type="button" class="link" @click="saveItem(item)">réessayer</button></template>
              <template v-else-if="item.__state === 'saved'">✓ Enregistré</template>
              <template v-else-if="item.__state === 'invalid'">Saisissez une désignation pour enregistrer cet objet</template>
            </small>
            <button type="button" class="link" @click="duplicateItem(index)">Dupliquer</button>
            <button type="button" class="link link--danger" @click="removeItem(index)">Supprimer</button>
          </footer>
        </article>
      </section>

      <datalist id="lot-platforms">
        <option v-for="platform in platformOptions" :key="platform" :value="platform" />
      </datalist>
      <datalist id="lot-countries">
        <option v-for="country in countryOptions" :key="country" :value="country" />
      </datalist>
    </template>
  </div>
</template>

<script>
import { photoErrorMessage, shrinkPhoto, shrinkPhotos } from "../../photos";
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config.js";
import ListPicker from "@/components/common/ListPicker.vue";

const SAVE_DELAY = 800;
const CONDITIONS = ["Neuf", "Très bon état", "Bon état", "État moyen", "Mauvais état"];
const WORKING_STATES = [
  { value: "untested", label: "Non testé" },
  { value: "working", label: "Fonctionne" },
  { value: "partial", label: "Partiel" },
  { value: "broken", label: "HS" },
];
const TRISTATE = [
  { value: "", label: "?" },
  { value: "true", label: "Oui" },
  { value: "false", label: "Non" },
];
const SOURCE_LABELS = { yahoo: "Yahoo Auctions", zenmarket: "ZenMarket", ebay: "eBay", vinted: "Vinted", leboncoin: "LeBonCoin", mercari: "Mercari", manual: "Manuel" };
// Fields sent to the API (local "__" fields stay in the browser)
const ITEM_FIELDS = ["_key", "title", "type", "model", "brand", "country", "condition", "working", "boxed", "manual", "full", "serial_matching", "included", "description"];

let localId = 0;
const nextId = () => `local-${++localId}`;

const toTristate = (value) => (value === true || value === "true" ? "true" : value === false || value === "false" ? "false" : "");

// Item coming from the API or from an analysis → editable item.
// reactive(): the saves update the same object the screen displays (with a
// plain object, the save status changed without the screen being notified).
const toEditable = (item, open = false) => reactive({
  ...item,
  title: item.title || item.name || "",
  type: item.type || "game",
  model: item.model || "",
  brand: item.brand || "",
  country: item.country || "",
  condition: item.condition || "",
  working: item.working || "untested",
  boxed: toTristate(item.boxed),
  manual: toTristate(item.manual),
  full: toTristate(item.full),
  serial_matching: toTristate(item.serial_matching),
  included: Array.isArray(item.included) ? [...item.included] : [],
  description: item.description || "",
  images: item.images || [],
  __id: item.__id || nextId(),
  __state: item._key ? "saved" : "new",
  __tag: "",
  __uploading: false,
  __open: open,
});

const toPayload = (item) => Object.fromEntries(ITEM_FIELDS.filter((field) => item[field] !== undefined).map((field) => [field, item[field]]));

export default {
  name: "AcquisitionItemsWorkspace",
  components: { ListPicker },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const offerId = computed(() => String(route.params.offerId || ""));

    const offer = ref({});
    const items = ref([]);
    const loading = ref(true);
    const loadError = ref("");
    const platformOptions = ref([]);
    const countryOptions = ref([]);

    const defaultsKey = computed(() => `prexens:lot-defaults:${offerId.value}`);
    const defaults = reactive({ platform: "", country: "", type: "game" });

    const quickTitle = ref("");
    const quickType = ref("game");
    const quickInput = ref(null);

    const pasteOpen = ref(false);
    const pasteText = ref("");
    const analysing = ref(false);
    const analysisError = ref("");
    const analysisMessage = ref("");
    const proposals = ref([]);

    const saveTimers = new Map();

    // ---------- Libellés ----------
    const sourceLabel = computed(() => SOURCE_LABELS[offer.value.source] || offer.value.source || "");
    const offerCost = computed(() => offer.value.transactionCosts?.totalCost || offer.value.cost || 0);
    const canExtract = computed(() => Boolean(offer.value.description || offer.value.title) && offer.value.source !== "manual");
    const pendingSaves = computed(() => items.value.some((item) => item.__state === "saving" || item.__state === "dirty"));
    const selectedProposals = computed(() => proposals.value.filter((proposal) => proposal.__selected));
    const typeLabel = (type) => ({ game: "Jeu", console: "Console", device: "Périphérique" }[type] || "Objet");
    const workingLabel = (value) => WORKING_STATES.find((state) => state.value === value)?.label || "";
    const formatMoney = (value) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
    const imageUrl = (item, image) => `${config.cdnUrl}/images/${item._key}/${String(image).split("/").pop()}`;
    const flagsFor = (item) => [
      { field: "boxed", label: "Boîte" },
      { field: "manual", label: "Notice" },
      { field: "full", label: "Complet" },
      ...(item.type === "console" ? [{ field: "serial_matching", label: "Serial matching" }] : []),
    ];

    // ---------- Chargement ----------
    const loadOptions = async (list, target) => {
      try {
        const { data } = await axios.get(`${config.apiUrl}/lists/${list}`);
        target.value = (Array.isArray(data) ? data : []).map((entry) => entry.name).filter(Boolean);
      } catch {
        target.value = [];
      }
    };

    const load = async () => {
      try {
        const [offerResponse, itemsResponse] = await Promise.all([
          axios.get(`${config.apiUrl}/offers/${offerId.value}`).catch(() => ({ data: { _key: offerId.value } })),
          axios.get(`${config.apiUrl}/offers/${offerId.value}/items`),
        ]);
        offer.value = offerResponse.data?.result || offerResponse.data || {};
        items.value = (itemsResponse.data?.result || []).map((item) => toEditable(item));
      } catch (error) {
        console.error("Impossible de charger le lot", error);
        loadError.value = "Réessayez depuis la liste des achats remportés.";
      } finally {
        loading.value = false;
      }

      // Valeurs du lot : mémorisées par achat sur cet appareil, sinon déduites
      try {
        const stored = JSON.parse(localStorage.getItem(defaultsKey.value) || "null");
        if (stored) Object.assign(defaults, stored);
      } catch {
        /* stockage indisponible : valeurs par défaut */
      }
      if (!defaults.country && ["yahoo", "zenmarket", "mercari"].includes(offer.value.source || "yahoo")) defaults.country = "Japon";
      if (!defaults.platform) {
        const platforms = items.value.map((item) => item.model).filter(Boolean);
        if (platforms.length) defaults.platform = platforms.sort((a, b) => platforms.filter((p) => p === b).length - platforms.filter((p) => p === a).length)[0];
      }
      quickType.value = defaults.type;
    };

    watch(defaults, () => {
      quickType.value = defaults.type;
      try {
        localStorage.setItem(defaultsKey.value, JSON.stringify({ ...defaults }));
      } catch {
        /* stockage indisponible */
      }
    });

    // ---------- Enregistrement objet par objet ----------
    // One save at a time per item, the next one after it: a new item gets its
    // key from its first save, the following ones update it (never a second
    // item created while the first save is still running)
    const inFlight = new Map();
    const saveItem = (item) => {
      clearTimeout(saveTimers.get(item.__id));
      const run = (inFlight.get(item.__id) || Promise.resolve()).then(() => sendItem(item));
      inFlight.set(item.__id, run);
      run.finally(() => {
        if (inFlight.get(item.__id) === run) inFlight.delete(item.__id);
      });
      return run;
    };
    const sendItem = async (item) => {
      if (item.__removed) return;
      if (!item.title.trim()) {
        item.__state = "invalid";
        return;
      }
      // Changes typed during the request keep the item "to save"
      const edits = item.__edits || 0;
      item.__state = "saving";
      try {
        const { data } = await axios.post(`${config.apiUrl}/offers/${offerId.value}/items`, {
          items: [toPayload(item)],
          removedItems: [],
        });
        const saved = data?.saved?.[0];
        if (saved) {
          item._key = saved._key;
          item.tested_at = saved.tested_at;
          item.included = saved.included || item.included;
        }
        if (item.__removed && item._key) {
          // Removed from the list while it was being created
          await axios.delete(`${config.apiUrl}/items/${item._key}`).catch(() => {});
          return;
        }
        item.__state = (item.__edits || 0) === edits ? "saved" : "dirty";
      } catch (error) {
        console.error("Enregistrement impossible", error);
        item.__state = "error";
      }
    };

    const allOpen = computed(() => items.value.length > 0 && items.value.every((item) => item.__open));
    const toggleAll = () => {
      const open = !allOpen.value;
      items.value.forEach((item) => {
        item.__open = open;
      });
    };
    const toggleItem = (item) => {
      item.__open = !item.__open;
    };
    const summaryMeta = (item) => {
      const parts = [typeLabel(item.type)];
      if (item.model) parts.push(item.model);
      if (item.working !== "untested") parts.push(workingLabel(item.working));
      if (item.boxed === "true") parts.push("boîte");
      if (item.manual === "true") parts.push("notice");
      if (item.included.length) parts.push(`${item.included.length} inclus`);
      return parts.join(" · ");
    };

    const queueSave = (item) => {
      item.__edits = (item.__edits || 0) + 1;
      item.__state = "dirty";
      clearTimeout(saveTimers.get(item.__id));
      saveTimers.set(item.__id, setTimeout(() => saveItem(item), SAVE_DELAY));
    };

    const setField = (item, field, value) => {
      item[field] = value;
      queueSave(item);
    };

    const newItem = (fields) =>
      toEditable(
        {
          type: defaults.type,
          model: defaults.platform,
          country: defaults.country,
          ...fields,
        },
        true
      );

    const addItems = async (newItems) => {
      const created = newItems.map(newItem);
      items.value.forEach((item) => {
        item.__open = false;
      });
      items.value.unshift(...created);
      await Promise.all(created.map(saveItem));
      return created;
    };

    const addQuickItem = async () => {
      if (!quickTitle.value) return;
      await addItems([{ title: quickTitle.value, type: quickType.value }]);
      quickTitle.value = "";
      await nextTick();
      quickInput.value?.focus();
    };

    const duplicateItem = (index) => {
      const source = items.value[index];
      const copy = newItem({ ...toPayload(source), _key: undefined, included: [...source.included] });
      delete copy._key;
      copy.images = [];
      source.__open = false;
      items.value.splice(index + 1, 0, copy);
      saveItem(copy);
    };

    const removeItem = async (index) => {
      const item = items.value[index];
      if (!window.confirm(`Supprimer « ${item.title || "cet objet"} » du lot ?`)) return;
      clearTimeout(saveTimers.get(item.__id));
      items.value.splice(index, 1);
      // Being created: deleted as soon as its creation ends (see sendItem)
      if (!item._key) {
        item.__removed = true;
        return;
      }
      try {
        await axios.delete(`${config.apiUrl}/items/${item._key}`);
      } catch (error) {
        console.error("Suppression impossible", error);
        items.value.splice(index, 0, item);
        window.alert("L’objet n’a pas pu être supprimé.");
      }
    };

    // ---------- Inclus (étiquettes) ----------
    const addTag = (item) => {
      const values = String(item.__tag || "").split(",").map((value) => value.trim()).filter(Boolean);
      if (!values.length) return;
      values.forEach((value) => {
        if (!item.included.some((tag) => tag.toLowerCase() === value.toLowerCase())) item.included.push(value);
      });
      item.__tag = "";
      queueSave(item);
    };
    const removeTag = (item, tagIndex) => {
      item.included.splice(tagIndex, 1);
      queueSave(item);
    };

    // ---------- Photos ----------
    const uploadPhotos = async (item, event) => {
      const files = [...(event.target.files || [])];
      event.target.value = "";
      if (!files.length) return;
      item.__uploading = true;
      try {
        if (!item._key) await saveItem(item);
        if (!item._key) throw new Error("Objet non enregistré");
        // Shrunk in the browser, then one per request (src/photos.js)
        for (const photo of await shrinkPhotos(files)) {
          const formData = new FormData();
          formData.append("files", photo);
          const { data } = await axios.post(`${config.apiUrl}/items/${item._key}/images`, formData);
          item.images = data?.images || item.images;
        }
      } catch (error) {
        console.error("Envoi des photos impossible", error);
        window.alert(photoErrorMessage(error));
      } finally {
        item.__uploading = false;
      }
    };

    // ---------- Analyse : annonce, dictée, liste ----------
    const showProposals = (data) => {
      analysisMessage.value = data?.message || "";
      proposals.value = (data?.result || []).map((proposal) => ({
        ...proposal,
        __id: nextId(),
        __selected: true,
        quantity: Math.max(1, Number(proposal.quantity) || 1),
      }));
      if (!proposals.value.length) analysisError.value = "Aucun objet n’a été reconnu dans ce texte.";
    };

    const runAnalysis = async (request) => {
      analysing.value = true;
      analysisError.value = "";
      try {
        const { data } = await request();
        showProposals(data);
      } catch (error) {
        analysisError.value = error.response?.data?.error || "L’analyse n’a pas pu aboutir. Réessayez dans un instant.";
      } finally {
        analysing.value = false;
      }
    };

    const lotContext = () => ({ platform: defaults.platform, country: defaults.country });

    const extractFromListing = () =>
      runAnalysis(() => axios.post(`${config.apiUrl}/offers/${offerId.value}/extract-items`, lotContext(), { timeout: 120000 }));

    // Photo of the whole lot: the vision model proposes the items it sees
    const analysePhoto = async (event) => {
      const [chosen] = event.target.files || [];
      event.target.value = "";
      if (!chosen) return;
      const file = await shrinkPhoto(chosen);
      const formData = new FormData();
      formData.append("photo", file);
      formData.append("platform", defaults.platform || "");
      formData.append("country", defaults.country || "");
      await runAnalysis(() => axios.post(`${config.apiUrl}/offers/${offerId.value}/analyse-photo`, formData, { timeout: 120000 }));
    };

    const analyseText = async () => {
      if (listening.value) stopDictation();
      const text = pasteText.value.trim();
      if (!text) return;
      await runAnalysis(() => axios.post(`${config.apiUrl}/offers/parse-items`, { text, ...lotContext() }, { timeout: 120000 }));
      if (!analysisError.value) pasteText.value = "";
    };

    const acceptProposals = async () => {
      const accepted = selectedProposals.value.flatMap((proposal) => {
        const fields = Object.fromEntries(Object.entries(proposal).filter(([key]) => !key.startsWith("__") && key !== "id" && key !== "quantity" && key !== "name"));
        if (!fields.model) fields.model = defaults.platform;
        if (!fields.country) fields.country = defaults.country;
        return Array.from({ length: proposal.quantity }, () => ({ ...fields, included: [...(fields.included || [])] }));
      });
      proposals.value = [];
      await addItems(accepted);
    };

    const togglePaste = () => {
      pasteOpen.value = !pasteOpen.value;
    };

    // ---------- Dictée (reconnaissance vocale du navigateur) ----------
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const speechSupported = Boolean(SpeechRecognition);
    const listening = ref(false);
    let recognition = null;

    const startDictation = () => {
      recognition = new SpeechRecognition();
      recognition.lang = "fr-FR";
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          if (event.results[i].isFinal) {
            const sentence = event.results[i][0].transcript.trim();
            if (sentence) pasteText.value = pasteText.value ? `${pasteText.value}\n${sentence}` : sentence;
          }
        }
      };
      recognition.onerror = (event) => {
        if (event.error === "not-allowed") analysisError.value = "Accès au micro refusé par le navigateur.";
      };
      recognition.onend = () => {
        listening.value = false;
      };
      recognition.start();
      listening.value = true;
      pasteOpen.value = true;
    };

    const stopDictation = () => {
      recognition?.stop();
      listening.value = false;
    };

    const toggleDictation = () => (listening.value ? stopDictation() : startDictation());

    // ---------- Navigation ----------
    const canLeave = () => !pendingSaves.value || window.confirm("Des modifications sont en cours d’enregistrement. Quitter quand même ?");
    const goBack = () => {
      if (!canLeave()) return;
      if (window.history.state?.back) router.back();
      else router.push({ name: "acquisitions" });
    };
    const beforeUnload = (event) => {
      if (!pendingSaves.value) return;
      event.preventDefault();
      event.returnValue = "";
    };

    onBeforeRouteLeave(() => canLeave());
    onMounted(() => {
      load();
      loadOptions("models", platformOptions);
      loadOptions("countries", countryOptions);
      window.addEventListener("beforeunload", beforeUnload);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("beforeunload", beforeUnload);
      if (listening.value) stopDictation();
      // Enregistre immédiatement les modifications encore en attente
      items.value.filter((item) => item.__state === "dirty").forEach(saveItem);
    });

    return {
      acceptProposals,
      addQuickItem,
      allOpen,
      summaryMeta,
      toggleAll,
      toggleItem,
      addTag,
      analysePhoto,
      analyseText,
      analysing,
      analysisError,
      analysisMessage,
      canExtract,
      conditions: CONDITIONS,
      countryOptions,
      defaults,
      duplicateItem,
      extractFromListing,
      flagsFor,
      formatMoney,
      goBack,
      imageUrl,
      items,
      listening,
      loadError,
      loading,
      offer,
      offerCost,
      offerId,
      pasteOpen,
      pasteText,
      pendingSaves,
      platformOptions,
      proposals,
      queueSave,
      quickInput,
      quickTitle,
      quickType,
      removeItem,
      removeTag,
      saveItem,
      selectedProposals,
      setField,
      sourceLabel,
      speechSupported,
      togglePaste,
      toggleDictation,
      tristate: TRISTATE,
      typeLabel,
      uploadPhotos,
      workingLabel,
      workingStates: WORKING_STATES,
    };
  },
};
</script>

<style scoped>
.lot-page {
  width: min(1100px, calc(100% - 32px));
  margin: 0 auto;
  padding: 28px 0 80px;
  color: #1d2939;
}
.page-state {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 40px;
  color: #657184;
}
.page-state--error strong { color: #b6423d; }

/* En-tête */
.lot-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}
.back {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d5dde5;
  border-radius: 8px;
  background: #fff;
  color: #536073;
  font-weight: 700;
  cursor: pointer;
}
.lot-header__title p {
  margin: 0 0 4px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.lot-header__title h1 {
  margin: 0;
  overflow: hidden;
  font-family: "Manrope", sans-serif;
  font-size: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lot-header__title span { color: #7a8697; font-size: 12px; }
.lot-header__stats { display: flex; flex-direction: column; align-items: flex-end; }
.lot-header__stats strong { font-size: 22px; }
.lot-header__stats span { color: #7a8697; font-size: 12px; }
.lot-header__stats .saving { color: #a86518; }
.lot-header__stats .saved { color: #1f7a5a; }

/* Cartes */
.card {
  margin-bottom: 14px;
  padding: 18px 20px;
  border: 1px solid #dfe5ec;
  border-radius: 12px;
  background: #fff;
}
.card h2, .items__header h2 { margin: 0; font-size: 15px; }
.hint { margin: 4px 0 12px; color: #7a8697; font-size: 12px; }
.defaults__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
label { display: flex; flex-direction: column; gap: 6px; color: #536073; font-size: 12px; font-weight: 700; }
input, select, textarea {
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #ccd5df;
  border-radius: 8px;
  background: #fff;
  color: #1d2939;
  font: inherit;
  font-size: 14px;
}
textarea { padding: 8px 10px; resize: vertical; }
input:focus, select:focus, textarea:focus { border-color: #4aa487; outline: none; box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.1); }

/* Boutons */
.primary, .secondary, .action {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.primary { border: 1px solid #182235; background: #182235; color: #fff; }
.secondary { border: 1px solid #cfd7e0; background: #fff; color: #536073; }
.primary:disabled, .action:disabled { opacity: 0.45; cursor: not-allowed; }
.action { border: 1px solid #d5dde5; background: #f7fafc; color: #2d394b; }
.action--ai { border-color: #b9d4ee; background: #edf4fb; color: #2e6098; }
.action--file { position: relative; display: inline-flex; align-items: center; flex-direction: row; gap: 0; font-size: 13px; }
.action--file input { position: absolute; inset: 0; min-height: 0; opacity: 0; cursor: pointer; }
.action--file.disabled { opacity: 0.45; pointer-events: none; }
.action--recording { border-color: #e2a3a0; background: #fdeeee; color: #b6423d; }
.link { padding: 0; border: 0; background: none; color: #2e6098; font-size: 12px; font-weight: 700; cursor: pointer; }
.link--danger { color: #b6423d; }

/* Ajout */
.add__actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
.paste { margin-bottom: 12px; }
.paste textarea { width: 100%; box-sizing: border-box; }
.paste__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 8px; color: #7a8697; }
.recording { color: #b6423d; font-weight: 700; }
.quick { display: grid; grid-template-columns: 1fr 160px auto; gap: 8px; }
.analysing { display: flex; align-items: center; gap: 8px; margin-top: 12px; color: #657184; font-size: 13px; }
.spinner { width: 14px; height: 14px; border: 2px solid #dbe2e9; border-top-color: #168a64; border-radius: 50%; animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.error { margin: 12px 0 0; color: #b6423d; font-size: 13px; }

/* Propositions */
.proposals { margin-top: 14px; border: 1px solid #cde7dd; border-radius: 10px; background: #f6fbf9; }
.proposals__header { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; padding: 12px 14px; border-bottom: 1px solid #dceee7; }
.proposals__header span { color: #657184; font-size: 12px; }
.proposals ul { margin: 0; padding: 0; list-style: none; }
.proposals li { display: flex; gap: 10px; align-items: flex-start; padding: 10px 14px; border-bottom: 1px solid #e6f1ec; }
.proposals li.review { background: #fff8ec; }
.proposals li input[type="checkbox"] { min-height: auto; width: 18px; height: 18px; margin-top: 9px; }
.proposal__body { display: flex; flex: 1; flex-direction: column; gap: 4px; min-width: 0; }
.proposal__title { font-weight: 700; }
.proposal__body small { color: #657184; font-size: 12px; }
.review-note { color: #a86518 !important; font-weight: 700; }
.proposals__footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 14px; }

/* Objets */
.items__header { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin: 22px 0 10px; }
.items__header span { color: #7a8697; font-size: 12px; }
.empty { padding: 28px; border: 1px dashed #d5dde5; border-radius: 12px; color: #7a8697; text-align: center; }
.item {
  margin-bottom: 12px;
  border: 1px solid #dfe5ec;
  border-left: 4px solid #dfe5ec;
  border-radius: 12px;
  background: #fff;
}
.item--saved { border-left-color: #27a979; }
.item--saving, .item--dirty { border-left-color: #d79b2b; }
.item--error { border-left-color: #c84642; }
.item__main { display: grid; grid-template-columns: 140px 1fr; gap: 16px; padding: 16px; }
.item__photos { display: flex; flex-direction: column; gap: 8px; }
.item__photos img, .item__nophoto {
  width: 140px;
  height: 140px;
  border-radius: 10px;
  object-fit: cover;
  background: #f1f4f7;
}
.item__nophoto { display: grid; place-items: center; color: #a3adba; font-size: 34px; }
.photo-button {
  position: relative;
  display: flex;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d5dde5;
  border-radius: 8px;
  background: #f7fafc;
  color: #2d394b;
  font-size: 12px;
  cursor: pointer;
}
.photo-button input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.photo-button.busy { opacity: 0.6; }
.item__fields { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.row--title { grid-template-columns: 1fr 150px; }
.title { font-weight: 700; }
.field { display: flex; flex-direction: column; gap: 5px; }
.field > span { color: #657184; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.segmented { display: flex; flex-wrap: wrap; gap: 4px; }
.segmented button {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #d5dde5;
  border-radius: 8px;
  background: #fff;
  color: #536073;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.segmented--small button { min-width: 44px; padding: 0 8px; }
.segmented button.active { border-color: #182235; background: #182235; color: #fff; }
.segmented button.active.w-working { border-color: #1f7a5a; background: #1f7a5a; }
.segmented button.active.w-partial { border-color: #a86518; background: #a86518; }
.segmented button.active.w-broken { border-color: #b6423d; background: #b6423d; }
.toggles { display: flex; flex-wrap: wrap; gap: 14px; }
.tags { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.tag { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 14px; background: #edf4fb; color: #2e6098; font-size: 12px; font-weight: 600; }
.tag button { border: 0; background: none; color: #2e6098; font-size: 14px; cursor: pointer; }
.tags input { flex: 1; min-width: 180px; }
.more summary { color: #2e6098; font-size: 12px; font-weight: 700; cursor: pointer; }
.more .row, .more textarea { margin-top: 8px; }
.more textarea { width: 100%; box-sizing: border-box; }
.item__footer { display: flex; align-items: center; gap: 14px; padding: 10px 16px; border-top: 1px solid #edf0f4; }
.item__footer .state { flex: 1; color: #7a8697; }

/* Ligne résumé (objet réduit) */
.items__header .link { margin-left: auto; }
.item__summary {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 0;
  border-radius: 12px;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.item--open .item__summary { border-bottom: 1px solid #edf0f4; border-radius: 12px 12px 0 0; }
.item__summary img, .item__thumb {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f4f7;
}
.item__thumb { display: grid; place-items: center; color: #a3adba; font-size: 18px; }
.item__summary-text { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 3px; }
.item__summary-text strong { overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.item__summary-text small { overflow: hidden; color: #7a8697; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.item__badge { min-width: 18px; font-weight: 800; text-align: center; }
.badge--saved { color: #1f7a5a; }
.badge--saving, .badge--dirty { color: #a86518; }
.badge--error, .badge--invalid { color: #b6423d; }
.chevron { color: #8994a3; }
.item--invalid { border-left-color: #c84642; }
.items .item { margin-bottom: 8px; }

/* Mobile : une carte par objet, gros boutons, photo en premier */
@media (max-width: 720px) {
  .lot-page { width: calc(100% - 20px); padding-top: 16px; }
  .lot-header { grid-template-columns: 1fr auto; }
  .back { grid-column: 1 / -1; justify-self: start; }
  .lot-header__title h1 { font-size: 18px; white-space: normal; }
  .defaults__grid { grid-template-columns: 1fr; }
  .quick { grid-template-columns: 1fr; }
  .add__actions .action { flex: 1 1 100%; min-height: 46px; font-size: 15px; }
  .item__main { grid-template-columns: 1fr; }
  .item__photos { flex-direction: row; align-items: center; }
  .item__photos img, .item__nophoto { width: 84px; height: 84px; }
  .photo-button { flex: 1; min-height: 46px; font-size: 14px; }
  .row, .row--title { grid-template-columns: 1fr; }
  .segmented button { flex: 1; min-height: 42px; }
  input, select { min-height: 44px; font-size: 16px; }
}
</style>
